
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Category, SubCategory } from '../../types';

interface CategoryCardProps {
    item: Category | SubCategory;
    onClick: () => void;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({ item, onClick }) => {
    const [imageError, setImageError] = useState(false);
    const { t } = useTranslation();

    // Determine if it's a subcategory by checking for categorie_id property
    const isSubCategory = 'categorie_id' in item;
    
    // Dynamic translation key based on type
    const translationKey = isSubCategory 
        ? `subcategories.${item.nom}` 
        : `categories.${item.nom}`;
    
    // Description translation key
    const descByContentKey: Record<string, string> = {
        'Recharges et forfaits pour tous les opérateurs.': 'ClientServices.Categories.section.mobileTopups',
        "Vous pouvez acheter de n'importe quel site international et tu paies par le Dinar Tunisien": 'ClientServices.Categories.section.internationalPayments',
        'Personnaliser vos document graphiques éducatifs administratifs ...': 'ClientServices.Categories.section.customDocuments',
        'Services de streaming, jeux et plus encore.': 'ClientServices.Categories.section.subscriptions',
        'Services de design graphique et création visuelle': 'ClientServices.SubCategories.section.graphicDesign',
        'Services et recharges Tunisie Télécom': 'ClientServices.SubCategories.section.tunisieTelecom',
        'Solutions de paiement pour transactions internationales': 'ClientServices.SubCategories.section.internationalTransactions',
        'Services et recharges Ooredoo Tunisie': 'ClientServices.SubCategories.section.ooredooTunisie',
        'Solutions de paiement pour transactions locales': 'ClientServices.SubCategories.section.localTransactions',
        'Services et recharges Orange Tunisie': 'ClientServices.SubCategories.section.orangeTunisie',
        'Services de streaming vidéo et musique': 'ClientServices.SubCategories.section.streamingVideoMusic',
        'Jeux vidéo et services gaming': 'ClientServices.SubCategories.section.gamingServices',
        'Applications et logiciels divers': 'ClientServices.SubCategories.section.appsAndSoftware',
        'Documents et ressources pédagogiques': 'ClientServices.SubCategories.section.educationalResources',
        'Génération de certificats et attestations': 'ClientServices.SubCategories.section.certificatesGeneration',
        'Traitement de documents administratifs': 'ClientServices.SubCategories.section.administrativeDocuments',
        'Services de paiement sécurisé en ligne': 'ClientServices.SubCategories.section.secureOnlinePayments',
        'Formations et cours en ligne': 'ClientServices.SubCategories.section.onlineCourses',
        'Outils pour améliorer la productivité': 'ClientServices.SubCategories.section.productivityTools',
    };
    const descKey = descByContentKey[item.description || ''] || `categories_desc.${item.nom}`;
    
    const displayName = t(translationKey, { defaultValue: item.nom });
    const displayDesc = t(descKey, { defaultValue: item.description || 'No description' });

    return (
        <div 
            onClick={onClick}
            className="group relative cursor-pointer overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border border-gray-200 dark:border-gray-700 flex flex-col"
        >
            <div className="relative h-32 w-full bg-gray-200 dark:bg-gray-700">
                {item.image_url && !imageError ? (
                    <img 
                        src={item.image_url}
                        alt={displayName} 
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="h-full w-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            </div>
            <div className="p-3">
                 <h3 className="font-bold text-md text-gray-800 dark:text-white truncate font-tajawal">{displayName}</h3>
                 <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1 font-tajawal">{displayDesc}</p>
            </div>
        </div>
    );
};
